/**
 * Phase 4 slice 4 — MTC Register. Read-only VIEW over GRN material-test-
 * certificate evidence (searchable register clients/auditors ask for).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileCheck2, ExternalLink } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import AuthImage from '../../components/safety/AuthImage';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';

import { formatDate as fmt } from '../../lib/format';

export default function MTCRegister() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [material, setMaterial] = useState('');
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);
  useEffect(() => {
    const q = [projectId && `project_id=${projectId}`, material && `material=${encodeURIComponent(material)}`].filter(Boolean).join('&');
    const t = setTimeout(() => apiGet(`/quality/mtc-register${q ? `?${q}` : ''}`).then((d) => setRows(d.register || [])).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [projectId, material]);

  return (
    <div>
      <PageHeader overline="Quality" title="MTC Register" subtitle="Material Test Certificates captured at goods receipt (GRN)" />
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[220px]" data-testid="mtc-filter-project">
          <option value="">All projects</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </select>
        <input value={material} onChange={(e) => setMaterial(e.target.value)} placeholder="Search material / item code" className="px-3 py-2 border border-slate-300 rounded-sm text-sm min-w-[220px]" data-testid="mtc-filter-material" />
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full dense-table" data-testid="mtc-table">
            <thead><tr><th>GRN</th><th>Material</th><th>Supplier</th><th className="text-right">Qty</th><th>Received</th><th className="text-center">MTC</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="text-center text-sm text-slate-400 py-10"><FileCheck2 size={20} className="inline mr-2 opacity-40" />No MTC records found.</td></tr>
                : rows.map((r, i) => (
                  <tr key={`${r.grn_id}-${i}`} className="hover:bg-slate-50" data-testid={`mtc-row-${i}`}>
                    <td className="text-xs font-display font-bold text-[#3b4872]">{r.grn_no}</td>
                    <td className="text-[13px] max-w-[240px] truncate">{r.material}<span className="text-slate-400"> · {r.item_code}</span></td>
                    <td className="text-xs text-slate-600">{r.supplier || '—'}</td>
                    <td className="text-right text-xs">{r.received_qty} {r.unit}</td>
                    <td className="text-xs text-slate-500">{fmt(r.received_at)}</td>
                    <td className="text-center">
                      {r.mtc_image_url ? <button onClick={() => setView(r)} className="text-[11px] font-bold text-[#3b4872] hover:underline" data-testid={`mtc-view-${i}`}>View MTC</button> : <span className="text-slate-300">—</span>}
                    </td>
                    <td><button onClick={() => navigate('/app/stores')} className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#3b4872]" data-testid={`mtc-grn-${i}`}>GRN <ExternalLink size={11} /></button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent data-testid="mtc-photo-dialog">
          <DialogHeader><DialogTitle>MTC · {view?.material}</DialogTitle>
            <DialogDescription>{view?.grn_no} · {view?.supplier} · {view?.mtc_remarks || 'No remarks'}</DialogDescription>
          </DialogHeader>
          {view?.mtc_image_url && <AuthImage src={view.mtc_image_url} alt="MTC" className="w-full max-h-[60vh] object-contain rounded-sm border border-slate-200" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
